import React, { useState, useEffect, useMemo } from 'react';
import { Search, AlertCircle, Loader2, ArrowRightLeft, ChevronDown, Sparkles, ChevronRight } from 'lucide-react';
import { supabase } from '../lib/supabase';
import { ProductDetailsModal } from './ProductDetailsModal';
import { StockTransferModal } from './StockTransferModal';

interface ConsolidatedStockItem {
  variante_id: number;
  nombre: string;
  variante: string;
  cantidades: Record<number, number>;
}

interface FilterState {
  productoVariante: string;
  proveedor: number | null;
  categoria: number | null;
}

interface Proveedor {
  id: number;
  nombre: string;
}

interface Categoria {
  id: number;
  nombre: string;
  path: string;
  parent_id?: number | null;
}

interface Deposito {
  id: number;
  nombre: string;
}

// CategoryHoverMenu related interfaces
interface CategoryHoverMenuProps {
  categories: Categoria[];
  categoriesMap: Map<number, Categoria[]>;
  onSelectCategory: (id: number | null) => void;
  selectedCategory: number | null;
}

interface CategoryColumnProps {
  categories: Categoria[];
  categoriesMap: Map<number, Categoria[]>;
  onSelectCategory: (id: number | null) => void;
  onHover: (id: number | null) => void;
  selectedCategory: number | null;
}

interface CategoryItemProps {
  category: Categoria;
  hasChildren: boolean;
  isSelected: boolean;
  onSelect: () => void;
  onHover: () => void;
}

interface ConsolidatedStockViewProps {
  companySchema: string;
  depositos: Deposito[];
  proveedores: Proveedor[];
  categorias: Categoria[];
}

export const ConsolidatedStockView: React.FC<ConsolidatedStockViewProps> = ({
  companySchema,
  depositos,
  proveedores,
  categorias,
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [stockItems, setStockItems] = useState<ConsolidatedStockItem[]>([]);
  const [selectedStockId, setSelectedStockId] = useState<number | null>(null);
  const [showTransferModal, setShowTransferModal] = useState(false);
  const [selectedVariantId, setSelectedVariantId] = useState<number | null>(null);
  const [filters, setFilters] = useState<FilterState>({
    productoVariante: '',
    proveedor: null,
    categoria: null,
  });
  const [showAiFilter, setShowAiFilter] = useState(false);

  useEffect(() => {
    fetchStockItems();
  }, [filters]);

  // Get parent ID from category path
  const getParentIdFromPath = (path: string): number | null => {
    // Format is /1/2/3/ where 3 is the current ID and 2 would be the parent
    const parts = path.split('/').filter(Boolean);
    if (parts.length <= 1) return null;
    return parseInt(parts[parts.length - 2], 10);
  };

  // Compute a categoriesMap for use with the CategoryHoverMenu
  const categoriesMap = useMemo(() => {
    const map = new Map<number, Categoria[]>();
    
    // Process each category to build parent-child relationships
    categorias.forEach((category) => {
      const parentId = getParentIdFromPath(category.path);
      
      // Add this category to its parent's children
      if (parentId !== null) {
        if (!map.has(parentId)) {
          map.set(parentId, []);
        }
        map.get(parentId)!.push(category);
      }
    });
    
    return map;
  }, [categorias]);

  // Root categories
  const rootCategories = useMemo(() => {
    return categorias.filter(category => {
      const segments = category.path.split('/').filter(Boolean);
      return segments.length === 1;
    });
  }, [categorias]);

  const fetchStockItems = async () => {
    try {
      setLoading(true);
      console.log('Fetching consolidated stock items with filters:', filters);
      setError(null);

      let query = supabase
        .schema(companySchema)
        .from('stock')
        .select(`
          variantes_id,
          local_id,
          cantidad_disponible,
          variantes!inner (
            nombre,
            productos!inner (
              nombre,
              categoria_id
            )
          )
        `);
      
      if (filters.proveedor) {
        query = query.eq('proveedores_id', filters.proveedor);
      }

      const { data, error: stockError } = await query;

      console.log('Raw consolidated stock data:', data);
      if (stockError) throw stockError;

      // Process data to consolidate by variant
      const stockMap = new Map<number, ConsolidatedStockItem>();

      console.log('Processing stock data...');
      data?.forEach(item => {
        const varianteId = item.variantes_id;
        
        if (!stockMap.has(varianteId)) {
          stockMap.set(varianteId, {
            variante_id: varianteId,
            nombre: item.variantes.productos.nombre,
            variante: item.variantes.nombre,
            cantidades: {},
            categoria_id: item.variantes.productos.categoria_id
          });
        }

        const stockItem = stockMap.get(varianteId)!;
        stockItem.cantidades[item.local_id] = item.cantidad_disponible;
      });

      // Get child category IDs if a category filter is applied
      let childCategoryIds: number[] = [];
      if (filters.categoria) {
        // Find the selected category
        const selectedCategory = categorias.find(cat => cat.id === filters.categoria);
        
        if (selectedCategory) {
          // Get all categories that are children of the selected category
          childCategoryIds = categorias
            .filter(cat => {
              // If this is the selected category itself, include it
              if (cat.id === filters.categoria) return true;
              
              // Check if the category's path includes the selected category's ID
              return cat.path.includes(`/${filters.categoria}/`);
            })
            .map(cat => cat.id);
        }
      }

      const consolidated = Array.from(stockMap.values())
        .filter(item => {
          // Text search filter
          const matchesProductVariante = !filters.productoVariante ? true : 
            (item.nombre.toLowerCase().includes(filters.productoVariante.toLowerCase()) || 
             item.variante.toLowerCase().includes(filters.productoVariante.toLowerCase()));

          // Category filter (including child categories)
          const matchesCategory = !filters.categoria || 
            (item.categoria_id !== null && (
              item.categoria_id === filters.categoria || 
              childCategoryIds.includes(item.categoria_id)
            ));
            
          return matchesProductVariante && matchesCategory;
        });

      console.log('Final consolidated stock items:', consolidated);
      setStockItems(consolidated);
    } catch (err) {
      console.error('Error fetching stock items:', err);
      setError(err instanceof Error ? err.message : 'Error loading stock items');
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (key: keyof FilterState, value: string | number | null) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  if (loading && !stockItems.length) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {error && (
        <div className="p-4 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2 text-red-600">
          <AlertCircle className="w-5 h-5 flex-shrink-0" />
          <p>{error}</p>
        </div>
      )}

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Product/Variant and Categoria Filters in a flex container */}
          <div className="md:col-span-2 flex space-x-4">
            {/* Unified Product/Variant Filter */}
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Producto/Variante
              </label>
              <div className="relative">
                <input
                  type="text"
                  value={filters.productoVariante}
                  onChange={(e) => handleFilterChange('productoVariante', e.target.value)}
                  placeholder="Buscar por producto o variante..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:border-transparent"
                />
                <Search className="w-5 h-5 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
              </div>
            </div>

            {/* Categoria Filter - Now with hierarchical menu */}
            <div className="flex-1 relative">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Categoría
              </label>
              <div className="relative w-full">
                <button
                  onClick={() => {
                    const menu = document.getElementById('consolidated-stock-category-menu');
                    if (menu) menu.classList.toggle('hidden');
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex items-center justify-between focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <span>{filters.categoria ? categorias.find(c => c.id === filters.categoria)?.nombre || 'Categoría' : 'Todas las categorías'}</span>
                  <ChevronDown className="w-4 h-4" />
                </button>
                
                <div
                  id="consolidated-stock-category-menu"
                  className="hidden absolute left-0 mt-2 z-20"
                >
                  <CategoryHoverMenu
                    categories={categorias}
                    categoriesMap={categoriesMap}
                    onSelectCategory={(id: number | null) => {
                      handleFilterChange('categoria', id);
                      document.getElementById('consolidated-stock-category-menu')?.classList.add('hidden');
                    }}
                    selectedCategory={filters.categoria}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Proveedor Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Proveedor
            </label>
            <select
              value={filters.proveedor || ''}
              onChange={(e) => handleFilterChange('proveedor', e.target.value ? Number(e.target.value) : null)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 focus:border-transparent"
            >
              <option value="">Todos los proveedores</option>
              {proveedores.map(proveedor => (
                <option key={proveedor.id} value={proveedor.id}>
                  {proveedor.nombre}
                </option>
              ))}
            </select>
          </div>
        </div>

        {/* AI Filter Section */}
        <div className="mt-4">
          <button
            onClick={() => setShowAiFilter(!showAiFilter)}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 rounded-lg transition-colors"
          >
            <Sparkles className="w-4 h-4" />
            Buscar con IA
            <ChevronDown
              className={`w-4 h-4 transition-transform ${showAiFilter ? 'rotate-180' : ''}`}
            />
          </button>
          
          {showAiFilter && (
            <div className="mt-4 p-4 border-2 border-dashed border-gray-300 rounded-lg">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Búsqueda en lenguaje natural
                  </label>
                  <input
                    type="text"
                    disabled
                    placeholder="Ejemplo: Mostrar productos con stock bajo y alta rotación"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 cursor-not-allowed"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-500 italic">
                    Integración futura con BackEnd
                  </span>
                  <button
                    disabled
                    className="px-4 py-2 bg-blue-100 text-blue-400 rounded-lg cursor-not-allowed"
                  >
                    Filtrar por IA
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stock Table */}
      <div className="bg-white rounded-lg shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Nombre
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Variante
                </th>
                {depositos.map(deposito => (
                  <th key={deposito.id} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {deposito.nombre}
                  </th>
                ))}
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Acciones
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {stockItems.map((item) => (
                <tr key={item.variante_id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.nombre}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                    {item.variante}
                  </td>
                  {depositos.map(deposito => (
                    <td key={deposito.id} className="px-6 py-4 whitespace-nowrap text-sm">
                      <span className={`${
                        item.cantidades[deposito.id] > 0
                          ? 'text-green-600 bg-green-50'
                          : 'text-red-600 bg-red-50'
                      } px-2 py-1 rounded-full`}>
                        {item.cantidades[deposito.id] || 0}
                      </span>
                    </td>
                  ))}
                  <td className="px-6 py-4 whitespace-nowrap flex gap-2">
                    <div className="flex gap-2">
                      <button
                        onClick={() => setSelectedStockId(item.variante_id)}
                        className="px-4 py-2 text-sm bg-blue-50 text-blue-600 hover:bg-blue-100 rounded-lg transition-colors font-medium shadow-sm"
                      >
                        Más info
                      </button>
                      <button
                        onClick={() => {
                          setSelectedVariantId(item.variante_id);
                          setShowTransferModal(true);
                        }}
                        className="px-4 py-2 text-sm bg-green-50 text-green-600 hover:bg-green-100 rounded-lg transition-colors font-medium shadow-sm flex items-center gap-2"
                      >
                        <ArrowRightLeft className="w-4 h-4" />
                        Transferir
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Product Details Modal */}
      <ProductDetailsModal
        isOpen={selectedStockId !== null}
        onClose={() => setSelectedStockId(null)}
        stockId={selectedStockId}
        companySchema={companySchema}
        localDestinoId={null}
      />

      {/* Stock Transfer Modal */}
      {selectedVariantId && (
        <StockTransferModal
          isOpen={showTransferModal}
          onClose={() => {
            setShowTransferModal(false);
            setSelectedVariantId(null);
          }}
          variantId={selectedVariantId}
          companySchema={companySchema}
          depositos={depositos}
          onSuccess={fetchStockItems}
        />
      )}
    </div>
  );
};

// CategoryHoverMenu component for a hover-based multi-column dropdown
const CategoryHoverMenu: React.FC<CategoryHoverMenuProps> = ({
  categories,
  categoriesMap,
  onSelectCategory,
  selectedCategory
}) => {
  // State to track which category columns are currently visible
  const [hoveredCategories, setHoveredCategories] = useState<number[]>([]);
  
  // Function to handle hovering over a category
  const handleCategoryHover = (categoryId: number | null, columnIndex: number) => {
    if (categoryId === null) {
      // If hovering away, remove all columns after the current index
      setHoveredCategories(prev => prev.slice(0, columnIndex));
    } else {
      // If hovering on a category, update columns after this index
      setHoveredCategories(prev => {
        const newHovered = [...prev.slice(0, columnIndex)];
        newHovered.push(categoryId);
        return newHovered;
      });
    }
  };
  
  // Root categories - those without parents
  const rootCats = useMemo(() => {
    return categories.filter(category => {
      const segments = category.path.split('/').filter(Boolean);
      return segments.length === 1;
    });
  }, [categories]);
  
  return (
    <div className="relative" style={{ height: '300px' }}>
      {/* First column with root categories */}
      <div className="absolute left-0 top-0 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-30 overflow-hidden">
        <div className="p-1">
          <button
            onClick={() => onSelectCategory(null)}
            className="w-full px-4 py-2 text-left hover:bg-gray-50 text-gray-800 rounded-md"
          >
            Todas las categorías
          </button>
          
          <CategoryColumn
            categories={rootCats}
            categoriesMap={categoriesMap}
            onSelectCategory={onSelectCategory}
            onHover={(id) => handleCategoryHover(id, 0)}
            selectedCategory={selectedCategory}
          />
        </div>
      </div>
      
      {/* Render additional columns for each hovered category */}
      {hoveredCategories.map((categoryId, index) => {
        // Get the child categories for this hovered category
        const childCategories = categoriesMap.get(categoryId) || [];
        
        if (childCategories.length === 0) return null;
        
        return (
          <div 
            key={categoryId}
            className="absolute top-0 w-64 bg-white rounded-md shadow-lg border border-gray-200 z-30 overflow-hidden"
            style={{ left: `${(index + 1) * 16}rem` }}
          >
            <div className="p-1">
              <CategoryColumn
                categories={childCategories}
                categoriesMap={categoriesMap}
                onSelectCategory={onSelectCategory}
                onHover={(id) => handleCategoryHover(id, index + 1)}
                selectedCategory={selectedCategory}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

// Column of category items
const CategoryColumn: React.FC<CategoryColumnProps> = ({
  categories,
  categoriesMap,
  onSelectCategory,
  onHover,
  selectedCategory
}) => {
  return (
    <div className="max-h-80 overflow-y-auto">
      {categories.map(category => (
        <CategoryItem
          key={category.id}
          category={category}
          hasChildren={categoriesMap.has(category.id)}
          isSelected={selectedCategory === category.id}
          onSelect={() => {
            onSelectCategory(category.id);
            document.getElementById('consolidated-stock-category-menu')?.classList.add('hidden');
          }}
          onHover={() => onHover(category.id)}
        />
      ))}
    </div>
  );
};

// Individual category item
const CategoryItem: React.FC<CategoryItemProps> = ({
  category,
  hasChildren,
  isSelected,
  onSelect,
  onHover
}) => {
  return (
    <div
      className={`flex items-center px-4 py-2 hover:bg-gray-100 transition-colors ${isSelected ? 'bg-blue-100 text-blue-800' : 'text-gray-800'}`}
      onMouseEnter={onHover}
    >
      <div 
        className="flex-1 cursor-pointer"
        onClick={onSelect}
      >
        {category.nombre}
      </div>
      {hasChildren && (
        <div className="flex items-center justify-center w-6">
          <ChevronRight className="w-4 h-4 text-gray-500" />
        </div>
      )}
    </div>
  );
};